// "Download PDF" / "Save to Document Control" for a technical view, and its
// thumbnail. The PDF is rendered on the server from this same view page
// (?print=1), so what prints is what is on screen, with the filter in the
// URL hash applied (back/utils/viewRender).
import React, { useEffect, useState } from "react";
import { Button, Space, Tooltip, message } from "antd";
import { FilePdfOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { downloadViewPdf, saveViewPdf, viewThumbnailUrl } from "api/documentation/viewRenderApi";

const currentHash = () => { try { return window.location.hash.replace(/^#/, ""); } catch (e) { return ""; } };

export default function ViewPdfActions({ projectId, viewId, title = "view", canSave = false, size = "small" }) {
  const [busy, setBusy] = useState("");
  const download = async () => {
    setBusy("pdf");
    try {
      const blob = await downloadViewPdf(projectId, viewId, currentHash());
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${String(title).replace(/[^\w.\- ]+/g, "").trim() || "view"} - technical view.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    } catch (e) { message.error(e.message); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const r = await saveViewPdf(projectId, viewId, currentHash());
      message.success(r.duplicate ? "Already in Document Control (same drawing)" : r.revised ? `Saved as revision ${r.doc.revisionNumber} in Document Control` : "Saved to Document Control");
    } catch (e) { message.error(e.message); } finally { setBusy(""); }
  };
  return (
    <Space size={6} className="g-noprint">
      <Tooltip title="A PDF of this view as it is on screen, filter included (vector drawing)">
        <Button size={size} icon={<FilePdfOutlined />} loading={busy === "pdf"} disabled={!!busy} onClick={download}>PDF</Button>
      </Tooltip>
      {canSave && (
        <Tooltip title="File this PDF in Document Control (one entry per view; each save is its next revision)">
          <Button size={size} icon={<CloudUploadOutlined />} loading={busy === "save"} disabled={!!busy} onClick={save}>Save to Doc Control</Button>
        </Tooltip>
      )}
    </Space>
  );
}

// The view's thumbnail; retries while the server renders it (202).
export function ViewThumbnail({ projectId, viewId, width = 240, style = {} }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false; let tries = 0; let timer = null; let objectUrl = null;
    const load = async () => {
      try {
        const u = await viewThumbnailUrl(projectId, viewId);
        if (cancelled) { if (u) URL.revokeObjectURL(u); return; }
        if (u) { objectUrl = u; setSrc(u); return; }
        if (++tries < 12) timer = setTimeout(load, 10000);
      } catch (e) { /* no thumbnail: the card shows without one */ }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [projectId, viewId]);
  if (!src) return <div style={{ width, height: Math.round(width * 0.62), background: "#F3F7F9", borderRadius: 6, ...style }} />;
  return <img src={src} alt="" style={{ width, borderRadius: 6, border: "1px solid #EFEFEF", ...style }} />;
}
